import { useQuery } from "@apollo/react-hooks";
import {
    Banner,
    Card,
    Page,
    Pagination,
    SkeletonBodyText,
    Stack,
} from "@shopify/polaris";
import moment from "moment";
import React, { useState } from "react";
import styled from "styled-components";
import { GET_ETSY_METRICS } from "../../graphql/queries/etsy-metric";
import { getDateRange } from "./date-range";
import EtsyListingStats from "./etsy-listing-stats";
import EtsyMetricStoreFilter from "./etsy-metric-store-filter";
import EtsyMetricTimeFilter from "./etsy-metric-time-filter";
import UpdatedAtTooltip from "./updated-at-tooltip";
import { formatMetricValue } from '../../helper';

const PAGE_SIZE = 20;
const METRIC_TYPES = [
    "Visits",
    "Orders",
    "ConversionRate",
    "Revenue",
    "Clicks",
    "Spend",
    "AdsPercentage",
    "TrueROAS",
];

const StoreBox = styled.details`
    summary {
        position: relative;
        cursor: pointer;
        padding: 1.6rem 6rem 1.6rem 2rem;
    }

    .store-updated-at {
        position: absolute;
        top: 1.6rem;
        right: 2rem;
    }

    summary:focus-visible {
        outline: 2px solid #5c6ac4;
        outline-offset: -2px;
    }

    .store-title {
        font-size: 1.6rem;
        font-weight: 600;
        overflow-wrap: anywhere;
    }

    .store-totals {
        display: flex;
        flex-wrap: wrap;
        gap: 1.2rem 2.4rem;
        margin-top: 1.2rem;
    }

    &[open] summary {
        border-bottom: 1px solid #dfe3e8;
    }
`;

const StoreBoxSkeleton = styled.div`
    padding: 1.6rem 2rem;

    .skeleton-title {
        width: 24rem;
        max-width: 100%;
    }

    .skeleton-totals {
        display: flex;
        flex-wrap: wrap;
        gap: 1.2rem 2.4rem;
        margin-top: 1.2rem;
    }

    .skeleton-total {
        width: 14rem;
        max-width: 100%;
    }
`;

function EtsyMetricStoreBoxSkeleton() {
    return (
        <Card>
            <StoreBoxSkeleton aria-hidden="true">
                <div className="skeleton-title">
                    <SkeletonBodyText lines={1} />
                </div>
                <div className="skeleton-totals">
                    {METRIC_TYPES.map((type) => (
                        <div className="skeleton-total" key={type}>
                            <SkeletonBodyText lines={1} />
                        </div>
                    ))}
                </div>
            </StoreBoxSkeleton>
        </Card>
    );
}

/* Previous box layout, retained for reference.
const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.6rem;
  padding: 1.6rem 2rem;

  .metric-box {
    min-width: 0;
    border: 1px solid #dfe3e8;
    border-radius: 4px;
    overflow: hidden;
  }

  .metric-title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.4rem 1.2rem;
    margin: 0;
    padding: 1.2rem 1.6rem;
    font-size: 1.6rem;
    font-weight: 600;
    border-bottom: 1px solid #dfe3e8;
  }

  .metric-total {
    font-size: 1.4rem;
    font-variant-numeric: tabular-nums;
  }

  .metric-entries {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    margin: 0;
    padding: 1.2rem 1.6rem;
  }

  .metric-entry {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.4rem 0.8rem;
    max-width: 100%;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    background: #f4f6f8;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  .metric-entry dt {
    color: #637381;
  }

  .metric-entry dd {
    margin: 0;
    font-weight: 600;
  }

  .metric-empty {
    padding: 1.6rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
*/

const MetricTableWrapper = styled.div`
    padding: 0;
    overflow-x: scroll;
    isolation: isolate;

    /* Keep a draggable scrollbar below the table, including on macOS. */
    &::-webkit-scrollbar {
        height: 16px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f2f3;
    }

    &::-webkit-scrollbar-thumb {
        border: 3px solid #f1f2f3;
        border-radius: 8px;
        background: #637381;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #454f5b;
    }

    @supports not selector(::-webkit-scrollbar) {
        scrollbar-width: auto;
        scrollbar-color: #637381 #f1f2f3;
    }

    &:focus-visible {
        outline: 2px solid #5c6ac4;
        outline-offset: -2px;
    }

    table {
        width: 100%;
        table-layout: fixed;
        border-collapse: separate;
        border-spacing: 0;
        font-variant-numeric: tabular-nums;
    }

    .metric-label-column {
        width: 14rem;
    }

    th,
    td {
        min-width: 11rem;
        padding: 1.2rem 1.6rem;
        text-align: right;
        white-space: nowrap;
        border-bottom: 1px solid #eeeeee;
    }

    thead th {
        border-bottom-color: #d9d9d9;
    }

    th:first-child {
        position: sticky;
        left: 0;
        z-index: 2;
        padding-left: 2rem;
        text-align: left;
        background-color: #fff;
    }

    th:first-child::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 1px;
        box-shadow: 4px 0 8px 1px rgba(33, 43, 54, 0.16);
        pointer-events: none;
        background-color: #f7f7f7;
    }

    th:last-child,
    td:last-child {
        padding-right: 2rem;
    }

    tbody tr:last-child th,
    tbody tr:last-child td {
        border-bottom: 0;
    }
`;

const metricLabels = {
    Orders: "Orders",
    Revenue: "Revenue",
    Visits: "Visits",
    ConversionRate: "Conversion rate",
    Clicks: "Ads Clicks",
    Spend: "Ads Spend",
    AdsPercentage: "Ads %",
    TrueROAS: "True ROAS",
};

const metricLabelsIndex = [
    "Visits",
    "Orders",
    "ConversionRate",
    "Revenue",
    "Clicks",
    "Spend",
    "AdsPercentage",
    "TrueROAS",
];

function hasCurrency(type) {
    return ["Revenue", "Spend"].includes(type);
}

function EtsyMetricStoreBox({ item, dateRange, storeAds }) {
    const [expanded, setExpanded] = useState(false);
    const dates = Array.from(
        new Set(
            item.data.reduce(
                (result, group) =>
                    result.concat(group.entries.map((entry) => entry.date)),
                [],
            ),
        ),
    );
    // .sort((a, b) => (a || "9999").localeCompare(b || "9999"));

    const suffix = (type) =>
        ["AdsPercentage", "ConversionRate"].includes(type) ? "%" : "";

    const metricLabelsMarkup = item.data.reduce((acc, group) => {
        const { type, total, currency } = group || {};
        acc[type] = (
            <span key={type}>
                {metricLabels[type] || type}:{" "}
                <strong>
                    {formatMetricValue({
                        value: total,
                        isCurrency: hasCurrency(type),
                        currency,
                        suffix: suffix(type),
                    })}
                </strong>
            </span>
        );

        return acc;
    }, {});

    return (
        <Card>
            <StoreBox
                onToggle={(event) => setExpanded(event.currentTarget.open)}
            >
                <summary>
                    <span className="store-title">
                        {item.store.title || item.store.id}
                    </span>
                    <span className="store-updated-at">
                        <UpdatedAtTooltip updatedAt={item.updatedAt} />
                    </span>
                    <span className="store-totals">
                        {metricLabelsIndex.map(
                            (key) => metricLabelsMarkup[key],
                        )}
                    </span>
                </summary>
                {/* Previous box layout:
        <MetricGrid>
          {METRIC_TYPES.map((type) => {
            const group = item.data.find((metric) => metric.type === type);
            const entries = group ? group.entries : [];

            return (
              <section className="metric-box" key={type}>
                <h3 className="metric-title">
                  <span>{metricLabels[type]}</span>
                  <span className="metric-total">
                    Total: {group ? formatMetricValue(group.total, type) : "—"}
                  </span>
                </h3>
                {entries.length ? (
                  <dl className="metric-entries">
                    {entries.map((entry, index) => (
                      <div
                        className="metric-entry"
                        key={`${entry.date}-${index}`}
                      >
                        <dt>{entry.date || "—"}</dt>
                        <dd>{formatMetricValue(entry.value, type)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="metric-empty">
                    No metrics found for this period.
                  </p>
                )}
              </section>
            );
          })}
        </MetricGrid>
        */}
                <MetricTableWrapper
                    tabIndex={dates.length ? 0 : undefined}
                    role="region"
                    aria-label={`Scrollable metrics for ${
                        item.store.title || item.store.id
                    }`}
                >
                    {dates.length ? (
                        <table
                            style={{ minWidth: `${14 + dates.length * 11}rem` }}
                            aria-label={`Metrics for ${
                                item.store.title || item.store.id
                            }`}
                        >
                            <colgroup>
                                <col className="metric-label-column" />
                                {dates.map((date) => (
                                    <col key={date || "undated"} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col">Metrics</th>
                                    {dates.map((date) => (
                                        <th scope="col" key={date || "undated"}>
                                            {date ? (
                                                <time
                                                    dateTime={date}
                                                    title={date}
                                                >
                                                    {moment(date).format(
                                                        "DD-MM-YYYY",
                                                    )}
                                                </time>
                                            ) : (
                                                "—"
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {METRIC_TYPES.map((type) => {
                                    const group = item.data.find(
                                        (metric) => metric.type === type,
                                    );

                                    let currency = group?.currency ?? "";

                                    const values = new Map(
                                        (group ? group.entries : []).map(
                                            (entry) => {
                                                if (!currency) {
                                                    currency = entry.currency;
                                                }

                                                return [
                                                    entry.date,
                                                    entry.value,
                                                ];
                                            },
                                        ),
                                    );

                                    const isCurrency = hasCurrency(type);
                                    const sf = suffix(type);

                                    return (
                                        <tr key={type}>
                                            <th scope="row">
                                                {metricLabels[type]}
                                            </th>
                                            {dates.map((date) => (
                                                <td key={date || "undated"}>
                                                    {values.has(date)
                                                        ? formatMetricValue({
                                                              value: values.get(
                                                                  date,
                                                              ),
                                                              isCurrency,
                                                              currency,
                                                              suffix: sf,
                                                          })
                                                        : "—"}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <p>No metrics found for this period.</p>
                    )}
                </MetricTableWrapper>
                {expanded && (
                    <EtsyListingStats
                        storeId={item.store.id}
                        dateRange={dateRange}
                        storeName={item.store.title || item.store.id}
                    />
                )}
            </StoreBox>
        </Card>
    );
}

function EtsyMetricStoreList({ stores, dateRange }) {
    return stores.map((item) => (
        <EtsyMetricStoreBox
            key={item.store.id}
            item={item}
            dateRange={dateRange}
        />
    ));
}

export default function EtsyMetric() {
    const [period, setPeriod] = useState(() => ({
        preset: "Last 30 days",
        dateRange: getDateRange("Last 30 days"),
        offset: 0,
        stores: [],
    }));
    const { data, loading, error, refetch } = useQuery(GET_ETSY_METRICS, {
        variables: {
            filter: {
                dateRange: period.dateRange,
                storeIds: period.stores.map((store) => store.value),
                limit: PAGE_SIZE,
                offset: period.offset,
            },
        },
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
    });
    const stores = (data && data.getEtsyMetricByStore) || [];

    return (
        <Page fullWidth title="Etsy Stats">
            <Card sectioned>
                <Stack alignment="leading">
                    <Stack.Item fill>
                        <EtsyMetricStoreFilter
                            selected={period.stores}
                            onChange={(stores) =>
                                setPeriod((previous) => ({
                                    ...previous,
                                    stores,
                                    offset: 0,
                                }))
                            }
                        />
                    </Stack.Item>
                    <EtsyMetricTimeFilter
                        value={period.preset}
                        dateRange={period.dateRange}
                        onChange={(preset, dateRange) =>
                            setPeriod((previous) => ({
                                ...previous,
                                preset,
                                dateRange,
                                offset: 0,
                            }))
                        }
                    />
                </Stack>
            </Card>
            {loading ? (
                <div
                    role="status"
                    aria-label="Loading Etsy metrics"
                    style={{ marginTop: "2rem" }}
                >
                    {Array.from({ length: 4 }, (_, index) => (
                        <EtsyMetricStoreBoxSkeleton key={index} />
                    ))}
                </div>
            ) : error ? (
                <Card sectioned>
                    <Banner
                        status="critical"
                        title="Could not load Etsy metrics"
                        action={{ content: "Retry", onAction: () => refetch() }}
                    >
                        <p>{error.message}</p>
                    </Banner>
                </Card>
            ) : stores.length ? (
                <EtsyMetricStoreList
                    key={JSON.stringify([
                        period.dateRange,
                        stores.map((item) => item.store.id),
                    ])}
                    stores={stores}
                    dateRange={period.dateRange}
                />
            ) : (
                <Card sectioned>
                    <p>No Etsy metrics found for this period.</p>
                </Card>
            )}
            <Card sectioned>
                <Pagination
                    hasPrevious={!loading && period.offset > 0}
                    onPrevious={() =>
                        setPeriod((previous) => ({
                            ...previous,
                            offset: Math.max(0, previous.offset - PAGE_SIZE),
                        }))
                    }
                    hasNext={!loading && !error && stores.length === PAGE_SIZE}
                    onNext={() =>
                        setPeriod((previous) => ({
                            ...previous,
                            offset: previous.offset + PAGE_SIZE,
                        }))
                    }
                />
            </Card>
        </Page>
    );
}
